import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowRight, ChevronDown, Search as SearchIcon } from "lucide-react";
import type { WpArticle } from "@/lib/posts.types";
import type { ApprovedTheme } from "@/lib/themes-public";
import { THEME_PAGES, themeByKey } from "@/lib/themes-public";
import type { ThemeKey } from "@/lib/theme-review";
import { cop31NotesSorted } from "@/lib/cop31-notes";
import { resizeImg } from "@/lib/img";
import { THEME_DOT, THEME_IMG } from "./theme-ui";
import { Breadcrumbs } from "./Breadcrumbs";
import { WRAP } from "./page";

/** Approved reference 02-articles-archive: title + theme orbit, search + sort, theme chips, row list, "load more". */
const STEP = 12;

function norm(s: string) {
  return s.toLocaleLowerCase("tr-TR").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

type Item = {
  key: string;
  to: "/yazilar/$slug" | "/cop31/notlar/$slug";
  slug: string;
  iso: string;
  date: string;
  theme: ThemeKey | null;
  note: boolean;
  title: string;
  excerpt: string;
  img: string | null;
  hay: string;
};

function Orbit() {
  return (
    <div aria-hidden className="pointer-events-none absolute right-0 top-0 hidden h-[260px] w-[420px] lg:block">
      <svg viewBox="0 0 420 260" className="absolute inset-0 h-full w-full overflow-visible text-ink/20" fill="none" stroke="currentColor" strokeWidth="1">
        <circle cx="150" cy="105" r="105" />
        <path d="M250 120 C 300 160, 330 175, 380 160" />
        <path d="M300 250 C 330 160, 380 110, 470 95" />
        <circle cx="45" cy="70" r="7" className="fill-theme-planet" stroke="none" />
        <circle cx="232" cy="120" r="5" className="fill-theme-economy" stroke="none" />
        <circle cx="290" cy="185" r="5" className="fill-theme-meaning" stroke="none" />
        <circle cx="262" cy="148" r="2" className="fill-theme-tech" stroke="none" />
      </svg>
      <img src={THEME_IMG.planet} alt="" width={280} height={280} className="absolute left-[80px] top-[35px] h-[140px] w-[140px] rounded-full object-cover" />
    </div>
  );
}

const archiveNotes = cop31NotesSorted.filter((n) => n.locale !== "en" && !n.internal);

/** Number of server-rendered archive pages (?sayfa=N) for the unfiltered list. */
export function archivePageCount(posts: WpArticle[]) {
  return Math.max(1, Math.ceil((posts.length + archiveNotes.length) / STEP));
}

export function TrArchive({ posts, themes, page = 1 }: { posts: WpArticle[]; themes: ApprovedTheme[]; page?: number }) {
  const [start, setStart] = useState((page - 1) * STEP);
  const [filter, setFilter] = useState<ThemeKey | "all">("all");
  const [order, setOrder] = useState<"new" | "old">("new");
  const [query, setQuery] = useState("");
  const [shownCount, setShownCount] = useState(STEP);

  const items = useMemo<Item[]>(() => {
    const map = new Map(themes.map((t) => [t.slug, t.primary]));
    const fromPosts: Item[] = posts.map((p) => {
      const theme = map.get(p.slug) ?? null;
      return {
        key: `p:${p.slug}`,
        to: "/yazilar/$slug",
        slug: p.slug,
        iso: p.isoDate,
        date: p.date,
        theme,
        note: false,
        title: p.title,
        excerpt: p.excerpt,
        img: p.img ? resizeImg(p.img, 360, { ar: 2 }) : theme ? THEME_IMG[theme] : null,
        hay: norm(`${p.title} ${p.excerpt}`),
      };
    });
    const fromNotes: Item[] = archiveNotes
      .map((n) => ({
        key: `n:${n.slug}`,
        to: "/cop31/notlar/$slug",
        slug: n.slug,
        iso: n.iso,
        date: n.dateTr,
        theme: null,
        note: true,
        title: n.titleTr,
        excerpt: n.summaryTr,
        img: THEME_IMG.planet,
        hay: norm(`${n.titleTr} ${n.summaryTr}`),
      }));
    return [...fromPosts, ...fromNotes];
  }, [posts, themes]);

  const filtered = useMemo(() => {
    const needle = query.trim() ? norm(query.trim()) : null;
    const list = items.filter((it) => (filter === "all" || it.theme === filter) && (!needle || it.hay.includes(needle)));
    const dir = order === "new" ? -1 : 1;
    return list.sort((a, b) => dir * (new Date(a.iso).getTime() - new Date(b.iso).getTime()));
  }, [items, filter, query, order]);

  const shown = filtered.slice(start, start + shownCount);
  const reset = () => {
    setStart(0);
    setShownCount(STEP);
  };
  const end = start + shown.length;
  const unfiltered = filter === "all" && !query.trim() && order === "new";
  const nextPage = Math.floor(end / STEP) + 1;

  const sortSelect = (
    <label className="relative inline-flex min-h-11 items-center">
      <span className="sr-only">Sırala</span>
      <select
        value={order}
        onChange={(e) => {
          setOrder(e.target.value as "new" | "old");
          reset();
        }}
        className="appearance-none bg-transparent py-2 pl-1 pr-7 font-serif text-[16px] text-ink focus:outline-none"
      >
        <option value="new">En yeni</option>
        <option value="old">En eski</option>
      </select>
      <ChevronDown aria-hidden className="pointer-events-none absolute right-1 size-4" />
    </label>
  );

  return (
    <>
      <section className={`${WRAP} relative pt-5 lg:pt-7`}>
        <Breadcrumbs items={[{ label: "Yazılar" }]} />
        <Orbit />
        <h1 className="relative mt-5 font-serif text-[48px] font-medium leading-none sm:text-[64px] lg:mt-6 lg:text-[76px]">Yazılar</h1>

        <div className="relative mt-6 flex items-center gap-6 lg:mt-8 lg:max-w-[52rem]">
          <label className="relative block flex-1">
            <span className="sr-only">Yazılarda ara</span>
            <SearchIcon aria-hidden className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-ink/70" />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                reset();
              }}
              placeholder="Yazılarda ara…"
              className="min-h-12 w-full rounded-full border border-ink/15 bg-paper pl-12 pr-4 text-base focus:border-ink focus:outline-none"
            />
          </label>
          <div className="hidden sm:block">{sortSelect}</div>
        </div>

        <div className="relative -mx-5 mt-5 flex gap-2.5 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
          <button
            type="button"
            aria-pressed={filter === "all"}
            onClick={() => {
              setFilter("all");
              reset();
            }}
            className={`inline-flex min-h-11 shrink-0 items-center rounded-full border px-5 text-[14px] ${filter === "all" ? "border-ink bg-ink text-paper" : "border-ink/15 bg-secondary/50 text-ink hover:border-ink"}`}
          >
            Tüm yazılar
          </button>
          {THEME_PAGES.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-pressed={filter === t.key}
              onClick={() => {
                setFilter(t.key);
                reset();
              }}
              className={`inline-flex min-h-11 shrink-0 items-center gap-2.5 rounded-full border px-5 text-[14px] ${filter === t.key ? "border-ink bg-ink text-paper" : "border-ink/15 bg-secondary/50 text-ink hover:border-ink"}`}
            >
              <span aria-hidden className={`size-2.5 rounded-full ${THEME_DOT[t.key]}`} />
              {t.title}
            </button>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between border-b border-ink/10 pb-3 sm:mt-6">
          <div className="sm:hidden">{sortSelect}</div>
          <p className="text-[14px] text-ink/80" aria-live="polite">{filtered.length} yazı</p>
        </div>
      </section>

      <section aria-label="Yazı listesi" className={`${WRAP} pb-12 lg:pb-16`}>
        {shown.length ? (
          <ul className="divide-y divide-ink/10">
            {shown.map((it) => {
              const t = themeByKey(it.theme);
              return (
                <li key={it.key}>
                  <Link
                    to={it.to}
                    params={{ slug: it.slug }}
                    className="group grid grid-cols-[78px_minmax(0,1fr)_auto] items-center gap-x-4 py-3 sm:grid-cols-[150px_minmax(0,1fr)_auto] lg:grid-cols-[176px_minmax(0,1.05fr)_minmax(0,1fr)_auto] lg:gap-x-8 lg:py-2"
                  >
                    {it.img ? (
                      <img src={it.img} alt="" loading="lazy" decoding="async" width={352} height={176} className="h-[62px] w-[78px] object-cover sm:h-[80px] sm:w-[150px] lg:h-[88px] lg:w-[176px]" />
                    ) : (
                      <span aria-hidden className="block h-[62px] w-[78px] bg-muted sm:h-[80px] sm:w-[150px] lg:h-[88px] lg:w-[176px]" />
                    )}
                    <span className="min-w-0 py-1 lg:py-3">
                      <span className="flex flex-col gap-0.5 text-[12px] text-ink/70 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:text-[13px]">
                        <span>{it.date}</span>
                        <span aria-hidden className="hidden sm:inline">·</span>
                        <span className="flex items-center gap-1.5">
                          {t ? <span aria-hidden className={`size-2 rounded-full ${THEME_DOT[t.key]}`} /> : <span aria-hidden className="size-2 rounded-full border border-ink" />}
                          {t ? t.title : "COP31 notu"}
                        </span>
                      </span>
                      <span className="mt-1 block text-balance font-serif text-[16px] leading-snug text-ink group-hover:text-clay sm:text-[21px] lg:text-[24px]">{it.title}</span>
                    </span>
                    <span className="hidden text-[14px] leading-relaxed text-ink/70 lg:line-clamp-3">{it.excerpt}</span>
                    <ArrowRight aria-hidden className="size-5 shrink-0 text-ink transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="py-10 text-ink/70">Aramanızla eşleşen yazı bulunamadı.</p>
        )}

        {end < filtered.length && (
          <div className="mt-6 border-t border-ink/10 pt-6">
            {/* A real link to the next server-rendered page, so crawlers reach every essay;
                for readers it keeps the in-place "load more" behaviour. */}
            <a
              href={`/yazilar?sayfa=${nextPage}`}
              onClick={(e) => {
                e.preventDefault();
                setShownCount((n) => n + STEP);
              }}
              className="mx-auto flex min-h-12 w-full max-w-md items-center justify-center gap-2 rounded-sm border border-ink/20 text-[15px] hover:border-ink"
            >
              Daha fazla yazı yükle <ArrowDown aria-hidden className="size-4" />
            </a>
            <p className="mt-3 text-center text-xs text-ink/60">{start > 0 ? `${start + 1}–${end}` : end} / {filtered.length} yazı gösteriliyor</p>
          </div>
        )}
        {start > 0 && unfiltered && (
          <p className="mt-6 text-center text-sm">
            <Link to="/yazilar" search={page > 2 ? { sayfa: page - 1 } : {}} className="underline underline-offset-4 hover:text-clay">← Önceki yazılar</Link>
          </p>
        )}
      </section>
    </>
  );
}
